import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faExternalLinkAlt, faColumns, faExpand, faSync, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { patientViewPath } from '../fetcher';
import { StatusEnum } from '../enums/statusEnum';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';
import { useNotifications, useAuth, UserAbility } from '@medprompt/shared';
import PendingReferralsList from './PendingReferralsList';
import { StatusBadge } from '@medprompt/shared';
import ConsultationPreviewModal from '../components/ConsultationPreviewModal';
import { PATIENT_DIARY_LAYOUT_SIDEBAR, PATIENT_DIARY_LAYOUT_TAB } from '@medprompt/shared';

const PatientDiaryHeaderActions = ({ layoutMode, onLayoutModeChange, showViewMore, onViewMore }) => (
  <div className="patient-diary-header-actions">
    {onLayoutModeChange && (
      <button
        className="view-more-button"
        onClick={() => onLayoutModeChange(
          layoutMode === PATIENT_DIARY_LAYOUT_SIDEBAR ? PATIENT_DIARY_LAYOUT_TAB : PATIENT_DIARY_LAYOUT_SIDEBAR
        )}
        title={layoutMode === PATIENT_DIARY_LAYOUT_SIDEBAR ? 'Move patient diary into sidebar tab' : 'Move patient diary to sidebar panel'}
      >
        <FontAwesomeIcon icon={layoutMode === PATIENT_DIARY_LAYOUT_SIDEBAR ? faColumns : faExpand} />
        <span>{layoutMode === PATIENT_DIARY_LAYOUT_SIDEBAR ? 'Move to tab' : 'Move to sidebar'}</span>
      </button>
    )}
    {showViewMore && (
      <button
        className="view-more-button"
        onClick={onViewMore}
        title="View full patient record"
      >
        <FontAwesomeIcon icon={faExternalLinkAlt} />
        <span>View More</span>
      </button>
    )}
  </div>
);

// Utility function to format time difference
const formatTimeDiff = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(diff / (1000 * 60 * 60 * 24 * 30));
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) return `${years}y`;
  if (months > 0) return `${months}m`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return 'now';
};

// Utility function to format timestamp to DD/MM/YYYY
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // month is 0-indexed
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

// Utility function to calculate age from DD/MM/YYYY date string
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  try {
    // Parse DD/MM/YYYY format
    const [day, month, year] = dateOfBirth.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day); // month is 0-indexed
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? `${age}y` : null;
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
};

// Utility function to format NHS ID with spacing (XXX XXX XXXX)
const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId;
  
  try {
    // Split at index 3 and 6, join with spaces
    return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
  } catch (error) {
    console.error('Error formatting NHS ID:', error);
    return nhsId; // Return original if formatting fails
  }
};

// Utility function to format DD/MM/YYYY to readable format like "June 16 2005"
const formatDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  try {
    // Parse DD/MM/YYYY format
    const [day, month, year] = dateOfBirth.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day); // month is 0-indexed
    
    // Get month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = monthNames[birthDate.getMonth()];
    const dayNum = birthDate.getDate();
    const yearNum = birthDate.getFullYear();
    
    return `${monthName} ${dayNum}, ${yearNum}`;
  } catch (error) {
    console.error('Error formatting date of birth:', error);
    return dateOfBirth; // Return original if parsing fails
  }
};

const normalizeExistingCodeTimestamp = (timestamp) => {
  if (timestamp == null) return '';
  if (typeof timestamp === 'number') return String(timestamp);
  const ms = Date.parse(timestamp);
  return Number.isNaN(ms) ? String(timestamp) : String(ms);
};

/** Sync key from ExistingCode SNOMED (`code.code`) + `timestamp`. */
export const getExistingCode = (existingCode) => {
  const snomed = existingCode?.code?.code;
  if (!snomed) return null;
  return `${snomed}|${normalizeExistingCodeTimestamp(existingCode?.timestamp)}`;
};

export const existingCodesMatch = (a, b) => {
  const keyA = getExistingCode(a);
  return keyA != null && keyA === getExistingCode(b);
};

const codesLength = (codes) => {
  if (!codes) return 0;
  if (typeof codes.length === 'number') return codes.length;
  if (typeof codes.size === 'number') return codes.size;
  return 0;
};

export const hasPatientCodesData = (codes) => (
  codes != null && (codesLength(codes.activeCodes) + codesLength(codes.significantPastCodes)) > 0
);

const DiarySectionPlaceholder = ({ error, loading, errorText }) => {
  if (error) {
    return (
      <div className="patient-diary-section-error">
        <div className="patient-data-error-pill">{errorText}</div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="patient-diary-section-loading" aria-busy="true">
        <div className="patient-diary-section-spinner" />
      </div>
    );
  }
  return null;
};

const PatientDiaryPanel = ({
  patientData,
  patientDataError,
  patientRecordData,
  patientRecordDataError,
  doc,
  onDocumentClick,
  onConsultationClick,
  onReferralClick,
  layoutMode,
  onLayoutModeChange,
  canEdit = false,
  existingPatientCodes = null,
  onExistingCodeClick = null,
  onRefreshPatientCodes = null,
  isRefreshingPatientCodes = false,
}) => {
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [consultationModalOpen, setConsultationModalOpen] = useState(false);
  const [selectedConsultationId, setSelectedConsultationId] = useState(null);

  // Check if user has permission to view consultations and documents
  const hasConsultRead = user?.authorities?.includes(UserAbility.CONSULT_READ.key);
  const hasDocsRead = user?.authorities?.includes(UserAbility.DOCS_READ.key);

  // Selected Existing Codes keyed by SNOMED + timestamp
  const selectedExistingCodeKeys = useMemo(() => {
    const selected = new Set();
    const addIfSelected = (codes) => {
      codes?.forEach((code) => {
        const key = getExistingCode(code);
        if (code?.isSelected && key) {
          selected.add(key);
        }
      });
    };
    addIfSelected(existingPatientCodes?.activeCodes);
    addIfSelected(existingPatientCodes?.significantPastCodes);
    return selected;
  }, [existingPatientCodes]);

  const handleProblemCodeClick = useCallback((code, category) => {
    if (!canEdit || !onExistingCodeClick) return;
    onExistingCodeClick(code, category);
  }, [canEdit, onExistingCodeClick]);

  const handleViewMore = useCallback(() => {
    if (patientData?.nhsId && patientData?.patientId) {
      navigate(patientViewPath(patientData.nhsId, patientData.patientId), {
        state: { 
          fromContext: 'diary', 
          returnPath: location.pathname,
          patient: patientData
        }
      });
    }
  }, [navigate, location.pathname, patientData]);

  // Copy to clipboard function
  const handleCopyToClipboard = useCallback((text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  }, [addNotification]);

  // Handle consultation click
  const handleConsultationClick = useCallback((consultationId) => {
    if (onConsultationClick) {
      onConsultationClick(consultationId);
    } else {
      setSelectedConsultationId(consultationId);
      setConsultationModalOpen(true);
    }
  }, [onConsultationClick]);

  // Memoize filtered document previews - must be before any early returns to follow rules of hooks
  const otherDocuments = useMemo(() => 
    patientRecordData?.documentPreviews?.filter(preview => preview.documentId !== doc?.documentId) || [],
    [patientRecordData?.documentPreviews, doc?.documentId]
  );

  // Memoize filtered consultation previews
  const otherConsultations = useMemo(() => 
    patientRecordData?.consultationPreviews?.filter(preview => preview.consultationId !== doc?.consultationId) || [],
    [patientRecordData?.consultationPreviews, doc?.consultationId]
  );

  const panelClassName = `patient-diary-panel${layoutMode === PATIENT_DIARY_LAYOUT_TAB ? ' in-tab' : ''}`;
  const recordLoading = !patientRecordDataError && !patientRecordData;
  const patientCodesData = patientRecordData?.patientCodesData;

  // Demographics come from the fast patient endpoint; block the panel on those only.
  if (patientDataError) {
    return (
      <div className={panelClassName}>
        <div className="patient-diary-header">
          <h3>Patient Diary</h3>
          <PatientDiaryHeaderActions
            layoutMode={layoutMode}
            onLayoutModeChange={onLayoutModeChange}
          />
        </div>
        <div className="patient-diary-content">
          <div className="patient-diary-error-container">
            <div className="patient-data-error-pill">
              Patient data failed to load
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className={panelClassName}>
        <div className="patient-diary-header">
          <h3>Patient Diary</h3>
          <PatientDiaryHeaderActions
            layoutMode={layoutMode}
            onLayoutModeChange={onLayoutModeChange}
          />
        </div>
        <div className="patient-diary-content">
          <div className="data-loading-container">
            <div className="data-loading-spinner"></div>
            <div className="data-loading-text">Loading patient data...</div>
          </div>
        </div>
      </div>
    );
  }

  const patient = patientData;

  const patientStatusMap = {
    ACTIVE: { label: 'Active', colorFamily: 'green', tooltip: null },
    DEDUCTION_PENDING: { label: 'Deduction Pending', colorFamily: 'amber', tooltip: 'This patient is in the process of being deregistered from the practice.' },
    INACTIVE: { label: 'Inactive', colorFamily: 'red', tooltip: 'This patient has been deregistered from the practice.' },
  };

  return (
    <div className={panelClassName}>
      <div className="patient-diary-header">
        <div className="patient-diary-header-title">
          <h3>{patient ? (patient.title ? `${patient.title} ${patient.name}` : patient.name) : 'Patient Diary'}</h3>
          {patient && (() => {
            const entry = patientStatusMap[patient.status || 'ACTIVE'] || patientStatusMap['ACTIVE'];
            return (
              <StatusBadge
                label={entry.label}
                colorFamily={entry.colorFamily}
                size="small"
                tooltip={entry.tooltip}
              />
            );
          })()}
        </div>
        <PatientDiaryHeaderActions
          layoutMode={layoutMode}
          onLayoutModeChange={onLayoutModeChange}
          showViewMore={Boolean(patient?.nhsId && patient?.patientId) && (hasDocsRead || hasConsultRead)}
          onViewMore={handleViewMore}
        />
      </div>
      <div className="patient-diary-content">
        {/* Patient Section */}
        <div className="patient-section">
          <h4>Patient Details</h4>
          <div className="patient-details">
            <div className="document-detail-row">
              <span className="document-detail-label">NHS Number:</span>
              <div className="detail-value-with-controls">
                <span className="document-detail-value">
                  {formatNhsId(patient?.nhsId) || <em>not found</em>}
                </span>
                {patient?.nhsId && patient?.nhsId.length === 10 && (
                  <button 
                    className="button copy-button" 
                    onClick={() => handleCopyToClipboard(patient.nhsId)}
                    title="Copy NHS number"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                )}
              </div>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Date of birth:</span>
              <span className="document-detail-value">{formatDateOfBirth(patient?.dateOfBirth) || <em>not found</em>}</span>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Age:</span>
              <span className="document-detail-value">{calculateAge(patient?.dateOfBirth) || <em>not found</em>}</span>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Sex:</span>
              <span className="document-detail-value">{patient?.sex || <em>not found</em>}</span>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Address:</span>
              <span className="document-detail-value">{patient?.address || <em>not found</em>}</span>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Phone:</span>
              <span className="document-detail-value">{patient?.phoneNumber || <em>not found</em>}</span>
            </div>
            <div className="document-detail-row">
              <span className="document-detail-label">Email:</span>
              <span className="document-detail-value">{patient?.email || <em>not found</em>}</span>
            </div>
          </div>
        </div>

        {/* Documents Section - Only visible with DOCS_READ ability */}
        {hasDocsRead && (
        <div className="documents-section">
          <h4>Other Documents</h4>
          <DiarySectionPlaceholder
            error={patientRecordDataError}
            loading={recordLoading}
            errorText="Other documents failed to load"
          />
          {!recordLoading && !patientRecordDataError && (
            otherDocuments.length === 0 ? (
              <div className="no-documents"><em>no other documents found</em></div>
            ) : (
              <div className="document-list">
                {otherDocuments.map(preview => (
                  <div 
                    key={preview.documentId} 
                    className="document-preview document-preview-clickable"
                    onClick={() => onDocumentClick && onDocumentClick(preview.documentId)}
                    title="Click to open this document"
                  >
                    <div className="document-preview-content">
                      <div className="document-preview-left">
                        <div className="document-preview-title">{preview.title || 'Untitled'}</div>
                        <StatusBadge
                          label={StatusEnum[preview.status]?.description || preview.status}
                          colorFamily={StatusEnum[preview.status]?.colorFamily || 'gray'}
                          size="small"
                        />
                      </div>
                      <span className="document-preview-time">
                        {formatTimestamp(preview.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        )}

        {/* Other Consultations Section - Only visible with CONSULT_READ ability */}
        {hasConsultRead && (
          <div className="consultations-section">
            <h4>Other Consultations</h4>
            <DiarySectionPlaceholder
              error={patientRecordDataError}
              loading={recordLoading}
              errorText="Other consultations failed to load"
            />
            {!recordLoading && !patientRecordDataError && (
              otherConsultations.length === 0 ? (
                <div className="no-consultations"><em>no other consultations found</em></div>
              ) : (
                <div className="consultation-list">
                  {otherConsultations.map(preview => (
                    <div 
                      key={preview.consultationId} 
                      className="consultation-preview consultation-preview-clickable"
                      onClick={() => handleConsultationClick(preview.consultationId)}
                      title="Click to preview this consultation"
                    >
                      <div className="consultation-preview-content">
                        <div className="consultation-preview-left">
                          <StatusBadge
                            label={ConsultationStatusEnum[preview.status]?.description || preview.status}
                            colorFamily={ConsultationStatusEnum[preview.status]?.colorFamily || 'gray'}
                            size="small"
                          />
                        </div>
                        <span className="consultation-preview-time">
                          {formatTimestamp(preview.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* Pending Referrals Section */}
        {hasDocsRead && (() => {
          const pendingReferrals = patientRecordData?.pendingReferrals || [];
          return (
            <div className="pending-referrals-section">
              <h4 className="pending-referrals-section-title">
                Pending Referrals
                {!recordLoading && !patientRecordDataError && pendingReferrals.length > 0 && (
                  <span className="tile-notification-badge">{pendingReferrals.length}</span>
                )}
              </h4>
              <DiarySectionPlaceholder
                error={patientRecordDataError}
                loading={recordLoading}
                errorText="Pending referrals failed to load"
              />
              {!recordLoading && !patientRecordDataError && (
                <PendingReferralsList
                  referrals={pendingReferrals}
                  onReferralClick={onReferralClick}
                />
              )}
            </div>
          );
        })()}

        {/* Problems Section */}
        <div className="problems-section">
          <div className="problems-section-header">
            <h4>Problems</h4>
            {onRefreshPatientCodes && !recordLoading && !patientRecordDataError && (
              <button
                className={`problems-refresh-button${isRefreshingPatientCodes ? ' is-fetching' : ''}`}
                onClick={onRefreshPatientCodes}
                disabled={isRefreshingPatientCodes}
                title={isRefreshingPatientCodes ? 'Refreshing problems from EMIS' : 'Refresh problems from EMIS'}
              >
                <FontAwesomeIcon icon={isRefreshingPatientCodes ? faSpinner : faSync} spin={isRefreshingPatientCodes} />
                {isRefreshingPatientCodes && <span>Fetching...</span>}
              </button>
            )}
          </div>
          <DiarySectionPlaceholder
            error={patientRecordDataError}
            loading={recordLoading}
            errorText="Problems failed to load"
          />
          {!recordLoading && !patientRecordDataError && (
            <>
              {/* Active Codes */}
              <div className="codes-subsection">
                <h5>Active</h5>
                {patientCodesData?.activeCodes?.length === 0 ? (
                  <div className="no-codes"><em>no active problems found</em></div>
                ) : (
                  <div className="document-codes-list">
                    {Array.from(patientCodesData?.activeCodes || []).map((code, index) => {
                      const syncKey = getExistingCode(code);
                      const isSelected = syncKey && selectedExistingCodeKeys.has(syncKey);
                      const isClickable = canEdit && !!onExistingCodeClick;
                      return (
                        <div
                          key={`active-${syncKey || code.codeId || index}`}
                          className={`code-item${isClickable ? ' code-item-clickable' : ''}${isSelected ? ' code-item-selected' : ''}`}
                          onClick={isClickable ? () => handleProblemCodeClick(code, 'Active') : undefined}
                          title={isClickable ? (isSelected ? 'Click to deselect this code' : 'Click to add/select this code') : undefined}
                          role={isClickable ? 'button' : undefined}
                          tabIndex={isClickable ? 0 : undefined}
                          onKeyDown={isClickable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleProblemCodeClick(code, 'Active');
                            }
                          } : undefined}
                        >
                          <div className="code-content">
                            <span className="code-term">{code.code.terms?.[0] || code.code.code}</span>
                            {code.code.isQof && (
                              <span className="qof-badge-mini" title="This is a QOF code">
                                ✓
                              </span>
                            )}
                          </div>
                          <span className="code-time">{formatTimestamp(code.timestamp)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Significant Past Codes */}
              <div className="codes-subsection">
                <h5>Significant Past</h5>
                {patientCodesData?.significantPastCodes?.length === 0 ? (
                  <div className="no-codes"><em>no significant past problems found</em></div>
                ) : (
                  <div className="document-codes-list">
                    {Array.from(patientCodesData?.significantPastCodes || []).map((code, index) => {
                      const syncKey = getExistingCode(code);
                      const isSelected = syncKey && selectedExistingCodeKeys.has(syncKey);
                      const isClickable = canEdit && !!onExistingCodeClick;
                      return (
                        <div
                          key={`past-${syncKey || code.codeId || index}`}
                          className={`code-item${isClickable ? ' code-item-clickable' : ''}${isSelected ? ' code-item-selected' : ''}`}
                          onClick={isClickable ? () => handleProblemCodeClick(code, 'Past') : undefined}
                          title={isClickable ? (isSelected ? 'Click to deselect this code' : 'Click to add/select this code') : undefined}
                          role={isClickable ? 'button' : undefined}
                          tabIndex={isClickable ? 0 : undefined}
                          onKeyDown={isClickable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleProblemCodeClick(code, 'Past');
                            }
                          } : undefined}
                        >
                          <div className="code-content">
                            <span className="code-term">{code.code.terms?.[0] || code.code.code}</span>
                            {code.code.isQof && (
                              <span className="qof-badge" title="This is a QOF code">
                                ✓
                              </span>
                            )}
                          </div>
                          <span className="code-time">{formatTimestamp(code.timestamp ?? 0)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      <ConsultationPreviewModal
        isOpen={consultationModalOpen}
        onClose={() => setConsultationModalOpen(false)}
        consultationId={selectedConsultationId}
        fromDocumentId={doc?.documentId}
      />
    </div>
  );
};

export default React.memo(PatientDiaryPanel);
